import { useEffect } from "react";
import { FormHelperText } from "@mui/material";
import DatePicker from "@components/DatePicker/DatePicker";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import {
  getEtcDateOrderError,
  normalizeDate,
} from "@features/vessel-call/vessel-call-status/PortServices/flow/utils";
import { useTranslation } from "react-i18next";

const getEtcError = ({
  etc,
  ets,
  ats,
  t,
}: {
  etc: Date | null;
  ets: Date | null;
  ats: Date | null;
  t: (key: string, options?: Record<string, string>) => string;
}) => {
  if (!etc) {
    return undefined;
  }

  if (etc < new Date()) {
    return t("form-validation.must-not-be-in-past", { label: "ETC" });
  }

  return getEtcDateOrderError({ etc, ets, ats });
};

interface ETCFieldProps {
  serviceDetails: {
    etc?: Date | null | string;
    ets?: Date | null | string;
    ats?: Date | null | string;
  };
}

const ETCField = ({
  serviceDetails: { etc: currentEtc, ets: currentEts, ats: currentAts },
}: ETCFieldProps) => {
  const { t } = useTranslation();
  const { setEtc, etc, errors, setErrors, ets, ats } = usePortServicesFlow();

  useEffect(() => {
    if (currentEtc instanceof Date) {
      setEtc(currentEtc);
    } else if (typeof currentEtc === "string" && currentEtc !== "") {
      setEtc(new Date(currentEtc));
    } else {
      setEtc(null);
    }
  }, [currentEtc, setEtc]);

  const effectiveEts = ets ?? currentEts;
  const effectiveAts = ats ?? currentAts;
  const normalizedEtc = normalizeDate(etc);
  const normalizedEts = normalizeDate(effectiveEts);
  const normalizedAts = normalizeDate(effectiveAts);

  const minEtcDate = [new Date(), normalizedEts, normalizedAts]
    .filter((value): value is Date => !!value)
    .reduce((latest, current) => {
      const nextMinute = new Date(current);
      nextMinute.setMinutes(nextMinute.getMinutes() + 1);

      return nextMinute.getTime() > latest.getTime() ? nextMinute : latest;
    }, new Date());

  return (
    <div className="flex flex-col gap-2">
      <CustomFormLabel data-testid={`change-status-ETC-label`}>ETC</CustomFormLabel>
      <DatePicker
        hasError={errors.etc !== undefined}
        value={normalizedEtc}
        onChange={(d) => {
          setEtc(d);
          setErrors((prev) => ({
            ...prev,
            etc: getEtcError({
              etc: d,
              ets: normalizedEts,
              ats: normalizedAts,
              t,
            }),
          }));
        }}
        testId="change-status-ETC-datepicker"
        timePicker={true}
        combinedInput={true}
        dateConstraint="not-past"
        minDate={minEtcDate}
      />
      {errors.etc && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {typeof errors.etc === "string" ? errors.etc : errors.etc?.toString()}
        </FormHelperText>
      )}
    </div>
  );
};

export default ETCField;
